import { Button, useToast } from "@design";
import { ImageUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "@/api";
import { Text } from "@/components/Text";
import { LOGO_TYPES } from "./companyForm";
import { checkLogo, useCompanyLogoUrl, useUploadLogo } from "./useCompanyLogo";

/** Object URL for a not-yet-uploaded file, revoked when it changes or unmounts. */
function usePendingPreview(file: File | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}

/**
 * Logo preview + upload. With a saved profile it uploads immediately. Before
 * the profile exists (the backend stores the logo on it) the file is held as
 * `pending` and the form uploads it right after creating the profile.
 */
export function LogoUpload({
  hasProfile,
  hasLogo,
  pending,
  onPending,
}: Readonly<{
  hasProfile: boolean;
  hasLogo: boolean;
  pending?: File | null;
  onPending?: (file: File | null) => void;
}>) {
  const { t } = useTranslation("adminCompany");
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const savedUrl = useCompanyLogoUrl(hasLogo);
  const pendingUrl = usePendingPreview(hasProfile ? null : pending);
  const url = pendingUrl ?? savedUrl;
  const upload = useUploadLogo();

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const problem = checkLogo(file);
    if (problem) {
      toast.error(t(`logo.errors.${problem}`));
      return;
    }
    if (!hasProfile) {
      onPending?.(file);
      return;
    }
    try {
      await upload.mutateAsync({ body: { file }, throwOnError: true });
      toast.success(t("logo.uploaded"));
    } catch (error) {
      toast.error(errorMessage(error, t("logo.failed")));
    }
  };

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center">
      <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-subtle bg-surface-secondary">
        {url ? (
          <img src={url} alt={t("logo.alt")} className="max-h-full max-w-full object-contain" />
        ) : (
          <ImageUp size={24} aria-hidden className="text-content-tertiary" />
        )}
      </div>
      <div className="flex flex-col items-start gap-1">
        <input
          ref={input}
          type="file"
          accept={LOGO_TYPES.join(",")}
          aria-label={t("logo.input")}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            void onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <Button variant="secondary" size="sm" loading={upload.isPending} onClick={() => input.current?.click()}>
          {hasLogo || pendingUrl ? t("logo.replace") : t("logo.upload")}
        </Button>
        <Text variant="caption" tone="tertiary">
          {hasProfile || !pending ? t("logo.hint") : t("logo.pending")}
        </Text>
      </div>
    </div>
  );
}
